// Bouton officiel « Se connecter avec Google » (Google Identity Services, projet Google Cloud CHENIA).
// L'identifiant client est PUBLIC ; le jeton reçu est vérifié par le serveur (POST /api/auth/google).
import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export const GOOGLE_WEB_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID || "291015608649-e48jt8puv7999g9lho9mqpt5lqdgupoc.apps.googleusercontent.com";

let gisPromise;
let gisLang;
// Le texte du bouton n'est traduit que via le paramètre « hl » du script GIS (l'option « locale » de renderButton est ignorée).
function loadGis(lang) {
  if (gisPromise && gisLang === lang) return gisPromise;
  gisLang = lang;
  gisPromise = new Promise((resolve, reject) => {
    document.querySelectorAll("script[data-gis]").forEach((el) => el.remove());
    const s = document.createElement("script");
    s.src = `https://accounts.google.com/gsi/client?hl=${encodeURIComponent(lang)}`;
    s.async = true;
    s.dataset.gis = "1";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gisPromise;
}

export default function GoogleSignInButton({ onSuccess, onError, text = "continue_with", testId = "google-auth-btn" }) {
  const ref = useRef(null);
  const { lang } = useLang();
  const [failed, setFailed] = useState(false);
  const cb = useRef({ onSuccess, onError });
  cb.current = { onSuccess, onError };

  useEffect(() => {
    let alive = true;
    loadGis(lang).then(() => {
      if (!alive || !ref.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_WEB_CLIENT_ID,
        callback: async ({ credential }) => {
          try {
            const { data } = await api.post("/auth/google", { id_token: credential });
            cb.current.onSuccess?.(data);
          } catch (e) {
            cb.current.onError?.(formatApiError(e.response?.data?.detail) || e.message);
          }
        },
      });
      ref.current.innerHTML = "";
      window.google.accounts.id.renderButton(ref.current, {
        type: "standard", theme: "outline", size: "large", shape: "pill", text, locale: lang,
        width: Math.min(ref.current.offsetWidth || 400, 400),
      });
    }).catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [lang, text]);

  if (failed) return null;
  return <div ref={ref} data-testid={testId} className="w-full flex justify-center min-h-[44px]" />;
}
